/**
 * @upmind/formguard — Client-side token manager
 *
 * Framework-agnostic token manager for FormGuard.
 * Fetches JWT tokens on demand, solves proof-of-work challenges,
 * and provides headers for protected requests.
 *
 * @example
 * ```ts
 * import { TokenManager } from '@upmind/formguard';
 *
 * const manager = new TokenManager();
 *
 * // Before submitting a protected request:
 * await manager.fetchToken();
 *
 * fetch('/login', {
 *   method: 'POST',
 *   headers: {
 *     'Content-Type': 'application/json',
 *     ...manager.getTokenHeaders(),
 *   },
 *   body: JSON.stringify({ email, password }),
 * });
 * ```
 *
 * @module @upmind/formguard
 */
const POW_BATCH_SIZE = 5_000;
/**
 * Compute a request hash for binding a token to a specific request.
 *
 * The hash is: `SHA-256(METHOD + "\n" + PATH + "\n" + BODY + "\n" + CONTENT_TYPE)`
 *
 * Both the client and edge must use this exact format.
 *
 * @param method — HTTP method, uppercase (e.g. "POST")
 * @param path — URL pathname only, no query string (e.g. "/oauth/access_token")
 * @param body — raw request body as string (empty string if none)
 * @param contentType — Content-Type header value (empty string if absent)
 * @returns hex-encoded SHA-256 hash
 */
export async function computeRequestHash(method, path, body, contentType) {
    const input = `${method}\n${path}\n${body}\n${contentType}`;
    const hash = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(input));
    return Array.from(new Uint8Array(hash))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
}
/**
 * Check whether a SHA-256 hash has at least `bits` leading zero bits.
 */
function hasLeadingZeroBits(hash, bits) {
    const fullBytes = Math.floor(bits / 8);
    const remaining = bits % 8;
    for (let i = 0; i < fullBytes; i++) {
        if (hash[i] !== 0)
            return false;
    }
    if (remaining > 0) {
        const mask = 0xff << (8 - remaining);
        if ((hash[fullBytes] & mask) !== 0)
            return false;
    }
    return true;
}
/**
 * Solve a proof-of-work challenge: find a nonce where
 * `SHA-256(token + ":" + nonce)` has `difficulty` leading zero bits.
 *
 * Runs in batches with event-loop yields to avoid blocking the UI.
 */
async function solveProof(token, difficulty) {
    const encoder = new TextEncoder();
    let nonce = 0;
    while (true) {
        const promises = [];
        for (let i = 0; i < POW_BATCH_SIZE; i++) {
            promises.push(crypto.subtle.digest("SHA-256", encoder.encode(`${token}:${nonce + i}`)));
        }
        const hashes = await Promise.all(promises);
        for (let i = 0; i < POW_BATCH_SIZE; i++) {
            if (hasLeadingZeroBits(new Uint8Array(hashes[i]), difficulty)) {
                return String(nonce + i);
            }
        }
        nonce += POW_BATCH_SIZE;
        // Yield to the event loop between batches
        await new Promise((resolve) => setTimeout(resolve, 0));
    }
}
/**
 * Manages on-demand FormGuard token fetching and proof-of-work solving.
 *
 * Call `fetchToken()` before each protected request. The method fetches a
 * fresh JWT from the API, solves any proof-of-work challenge, and stores
 * the result for `getTokenHeaders()`.
 */
export class TokenManager {
    /** Base URL for the API. Empty string means same domain. */
    baseUrl;
    /** API endpoint path to fetch tokens from. */
    endpoint;
    /** Callback for new tokens. */
    onToken;
    /** Callback for fetch errors. */
    onError;
    _fetchOptions;
    _token = null;
    _proof = null;
    _debug = false;
    constructor(options = {}) {
        this.baseUrl = options.baseUrl ?? "https://api.upmind.io";
        this.endpoint = options.endpoint ?? "/api/formguard/token";
        this.onToken = options.onToken ?? null;
        this.onError = options.onError ?? null;
        this._fetchOptions = options.fetchOptions ?? {};
        try {
            this._debug = !!localStorage.getItem("formguard_debug");
        }
        catch {
            // localStorage unavailable
        }
    }
    /**
     * Fetch a fresh token from the API endpoint, optionally bound to a
     * request hash, and solve the proof-of-work challenge if required.
     *
     * @param requestHash — hex-encoded SHA-256 hash of the request to bind to
     * @throws {Error} if the fetch or proof-of-work fails
     */
    async fetchToken(requestHash) {
        try {
            const headers = {
                Accept: "application/json",
                ...this._fetchOptions.headers,
            };
            try {
                const debugSecret = localStorage.getItem("formguard_debug");
                if (debugSecret)
                    headers["X-FormGuard-Debug"] = debugSecret;
            }
            catch {
                // localStorage unavailable (e.g. sandboxed iframe) — skip
            }
            let url = `${this.baseUrl}${this.endpoint}`;
            if (requestHash) {
                url += `?request_hash=${requestHash}`;
            }
            const res = await fetch(url, {
                method: "GET",
                ...this._fetchOptions,
                headers,
            });
            if (!res.ok) {
                throw new Error(`FormGuard token fetch failed: ${res.status} ${res.statusText}`);
            }
            const data = await res.json();
            if (!data.token || typeof data.token !== "string") {
                throw new Error("FormGuard: invalid token response — expected { token: string }");
            }
            this._token = data.token;
            this._proof = null;
            // Solve proof-of-work if the server requires it
            if (data.difficulty && data.difficulty > 0) {
                const t0 = performance.now();
                this._proof = await solveProof(data.token, data.difficulty);
                if (this._debug) {
                    const ms = (performance.now() - t0).toFixed(0);
                    console.log(`[FormGuard] PoW solved in ${ms}ms`, { difficulty: data.difficulty, nonce: this._proof });
                }
            }
            this.onToken?.(data.token);
            return data.token;
        }
        catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            this.onError?.(error);
            throw error;
        }
    }
    /**
     * Get the current token string, or `null` if no token has been fetched.
     */
    getToken() {
        return this._token;
    }
    /**
     * Get the current proof-of-work nonce, or `null` if not applicable.
     */
    getProof() {
        return this._proof;
    }
    /**
     * Get the token and proof as HTTP headers for a protected request.
     *
     * @example
     * ```ts
     * await manager.fetchToken();
     * fetch('/login', {
     *   method: 'POST',
     *   headers: { 'Content-Type': 'application/json', ...manager.getTokenHeaders() },
     *   body: JSON.stringify({ email, password }),
     * });
     * ```
     *
     * @returns `{ "X-FormGuard-Token": "eyJ...", "X-FormGuard-Proof": "12345" }` or `{}`
     */
    getTokenHeaders() {
        if (!this._token)
            return {};
        const headers = {
            "X-FormGuard-Token": this._token,
        };
        if (this._proof) {
            headers["X-FormGuard-Proof"] = this._proof;
        }
        return headers;
    }
}
//# sourceMappingURL=index.js.map